import * as React from 'react'
import * as Api from 'src/api'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { CREDIT_TYPE_DOCTORAL, CREDIT_TYPE_PASS_FAIL } from './constants'

interface ScoredChoice {
  readonly creditType: number
  readonly score: number
}

/** Formats a raw choice score: doctoral and pass/fail choices store an option id instead of a number. */
export type ScoreFormatter = (choice: ScoredChoice) => string | number

export function useChoiceScoreFormatter(): ScoreFormatter {
  const headers = useAuthenticatedHeaders()

  const { data: scoreOptionsResponse } = useApi({
    endpoint: Api.getAdministratorListsScoreOptions,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const doctoralScoreNames = React.useMemo(
    () => new Map((scoreOptionsResponse?.data.doctoral ?? []).map((o) => [o.id, o.name])),
    [scoreOptionsResponse?.data.doctoral]
  )

  const passFailScoreNames = React.useMemo(
    () => new Map((scoreOptionsResponse?.data.passFail ?? []).map((o) => [o.id, o.name])),
    [scoreOptionsResponse?.data.passFail]
  )

  return React.useCallback<ScoreFormatter>(
    (choice) => {
      if (choice.creditType === CREDIT_TYPE_DOCTORAL) return doctoralScoreNames.get(choice.score) ?? choice.score
      if (choice.creditType === CREDIT_TYPE_PASS_FAIL) return passFailScoreNames.get(choice.score) ?? choice.score
      return choice.score
    },
    [doctoralScoreNames, passFailScoreNames]
  )
}
